import React, { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ConfigProvider, theme, App as AppAntd, Modal, Flex, Typography } from "antd";
import pt_BR from "antd/locale/pt_BR";

import { TypeSessao } from "./Models/Sessao";
import { SessaoContext } from "./Contexts/SessaoContext";
import { MasterPage } from "./Views/Sistema/MasterPage";
import { Login } from "./Views/Sistema/Login";
import { Splash } from "./Views/Sistema/Splash";

import { OpcoesDoUsuarioContext } from "./Contexts/OpcoesDoUsuarioContext";
import { TypeOpcoesDoUsuario } from "./Models/OpcoesDoUsuario";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { DefaultQueryClientConfig } from "./Defaults";

import "./Styles/Listas.css";
import "./Styles/AntD/Collapse.css";
import "./Styles/AntD/FormItem.css";
import "./Styles/Tour.css";
import { NavegacaoContextProvider } from "./Contexts/NavegacaoContext";

export const queryClient = new QueryClient({ ...DefaultQueryClientConfig });

const temaAtual = localStorage.getItem("darkTheme");

const App: React.FC = () => {
    const [sessao, setSessao] = useState<TypeSessao | null>(null);

    const [opcoesDoUsuario, setOpcoesDoUsuario] = useState<TypeOpcoesDoUsuario | null>({
        ocultarPopOverDeAjuda: (() => {
            let c = localStorage.getItem("_hide_popup");
            if (c == undefined) return false;

            let dataDaUltimaConfiguracao = new Date(Number(c));
            let dataAtual = new Date();
            let intervaloMaximoEmSegundos = 60 * 60; // 1 hora

            if ((dataAtual.getTime() - dataDaUltimaConfiguracao.getTime()) / 1000 < intervaloMaximoEmSegundos) {
                return true;
            }

            return false;
        })(),
    });

    useEffect(() => {
        if (sessao?.usuario) {
            setSessao((prev) => ({ ...prev, permissoes: sessao.usuario?.permissoes.map((p) => p.nome) }));
        }
    }, [sessao?.usuario]);

    const [temaEscuro, setTemaEscuro] = useState(
        temaAtual ? temaAtual === "1" : window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    );

    function trocarTema(escuro: boolean) {
        setTemaEscuro(escuro);
        localStorage.setItem("darkTheme", escuro ? "1" : "0");
    }

    return (
        <AppAntd>
            <ConfigProvider
                locale={pt_BR}
                theme={{
                    algorithm: temaEscuro ? theme.darkAlgorithm : theme.defaultAlgorithm,
                    cssVar: true,
                    components : {
                        Form : {
                            itemMarginBottom : 8
                        }
                    }
                }}
                datePicker={{
                    style: {
                        width: "100%",
                    },
                }}
                rangePicker={{
                    style: {
                        width: "100%",
                    },
                }}
            >
                <QueryClientProvider client={queryClient}>
                    <SessaoContext.Provider value={{ sessao, setSessao }}>
                        <OpcoesDoUsuarioContext.Provider value={{ opcoesDoUsuario, setOpcoesDoUsuario }}>
                            <Modal
                                open={!sessao?.token && sessao?.usuario !== undefined}
                                closable={false}
                                footer={false}
                                title={
                                    <>
                                        Sessão expirada. <br />
                                        Por favor, refaça o login.
                                    </>
                                }
                                styles={{
                                    header: {
                                        textAlign: "center",
                                        marginBottom: "1em",
                                    },
                                }}
                            >
                                <Flex align="center" justify="center">
                                    <Login temaEscuro={temaEscuro} />
                                </Flex>
                            </Modal>

                            {process.env.REACT_APP_AMBIENTE === "DESENVOLVIMENTO" ||
                                (process.env.REACT_APP_AMBIENTE === "HOMOLOGACAO" && (
                                    <Typography.Title
                                        level={3}
                                        type="danger"
                                        style={{ position: "sticky", top: 0, backgroundColor: "#fff", margin: 0, textAlign: "center", width: "100%" }}
                                    >
                                        {process.env.REACT_APP_DESC_AMBIENTE}
                                    </Typography.Title>
                                ))}

                            <BrowserRouter>
                                <NavegacaoContextProvider>
                                    {(() => {
                                        if (sessao == undefined) {
                                            return <Splash temaEscuro={temaEscuro} />;
                                        }

                                        if (sessao.token === undefined && sessao.usuario === undefined) {
                                            return <Login telaInteira temaEscuro={temaEscuro} />;
                                        }

                                        return <MasterPage temaEscuro={temaEscuro} setTemaEscuro={trocarTema} />;
                                    })()}
                                </NavegacaoContextProvider>
                            </BrowserRouter>
                        </OpcoesDoUsuarioContext.Provider>
                    </SessaoContext.Provider>
                    {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
                </QueryClientProvider>
            </ConfigProvider>
        </AppAntd>
    );
};

export default App;
